// Who the sparring partner is, in words, for the line on the board.
//
// There are two different opponents and the header used to claim only one of them: a catalog line
// answers from a book generated once at a fixed rating band, while a line of the user's is answered
// live by the Lichess explorer with whatever /settings says. Saying "club players (1600-2000)" over
// a configuration set to 2000-2400 is how that confusion looked.

/** One rating band of the catalog index: { label, ratings: "1600,1800,2000", speeds }. */
export interface CatalogBand {
    label?: string;
    ratings?: string;
    speeds?: string;
}

/** The sparring filters as /settings saves them. */
export interface SparringConfig {
    sparringSkill?: string;
    ratings?: string[];
    speeds?: string[];
}

/** "1600,1800,2000" -> "1600-2000"; a single rating stays as it is. */
export function ratingRange(ratings: (string | number)[] | string | null | undefined): string | null {
    const list = (typeof ratings === "string" ? ratings.split(",") : ratings || [])
        .map((r) => Number(String(r).trim()))
        .filter((r) => Number.isFinite(r) && r > 0)
        .sort((a, b) => a - b);
    if (!list.length) return null;
    const low = list[0]!;
    const high = list[list.length - 1]!;
    return low === high ? `${low}` : `${low}-${high}`;
}

/** What the catalog book was generated from: "club players (1600-2000)". */
export function bandOpponent(band: CatalogBand | null | undefined): string {
    const label = (band?.label || "club players").toLowerCase();
    const range = ratingRange(band?.ratings);
    return range ? `${label} (${range})` : label;
}

/** What /settings asks the explorer for: "2000-2400 (blitz, rapid)", "masters". */
export function configuredOpponent(config: SparringConfig | null | undefined): string {
    if (config?.sparringSkill === "master") return "masters";
    const range = ratingRange(config?.ratings);
    const speeds = (config?.speeds || []).filter(Boolean);
    const level = range ? `${range} players` : "players of any rating";
    return speeds.length ? `${level} (${speeds.join(", ")})` : level;
}

export interface OpponentLine {
    /** "You play White, against club players (1600-2000)" */
    text: string;
    /** The small print under it, when the opponent is not the configured one. */
    note: string | null;
    noteTitle: string | null;
}

/**
 * The sentence under the line's name. A catalog line names the band its book really holds, and says
 * so, because /settings has no say over it.
 */
export function opponentLine({ source, color, band, config }: {
    source: string;
    color: string;
    band?: CatalogBand | null;
    config?: SparringConfig | null;
}): OpponentLine {
    const side = color === "black" ? "Black" : "White";
    if (source === "catalog") {
        return {
            text: `You play ${side}, against ${bandOpponent(band)}`,
            note: "Catalog line · fixed opponent",
            noteTitle: "This line answers from a book generated once at that rating band. "
                + "The level you choose in Settings applies to your own lines, which are answered live by Lichess.",
        };
    }
    return {
        text: `You play ${side}, against ${configuredOpponent(config)}`,
        note: null,
        noteTitle: null,
    };
}
